import React, { createContext, useContext, ReactNode } from 'react';
import { useCookieConsent, UseCookieConsentReturn, CookiePreferences } from '../hooks/useCookieConsent';

// Define a base context value that matches the hook's return but allows for undefined initially
interface CookieConsentContextType extends Partial<UseCookieConsentReturn> {
  // We can add specific overrides or additional properties for the context if needed later
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const CookieConsentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const cookieConsentValue = useCookieConsent();

  return (
    <CookieConsentContext.Provider value={cookieConsentValue}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsentContext = (): UseCookieConsentReturn => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsentContext must be used within a CookieConsentProvider');
  }
  // We ensure that the context, when used, will provide the full UseCookieConsentReturn interface.
  // The hook itself initializes all these values, so they should be present.
  return context as UseCookieConsentReturn; 
};

// Helper function to directly check if a category is enabled via context
// This is useful for components that only need to check consent for a specific category
export const isCookieCategoryEnabled = (category: keyof Omit<CookiePreferences, 'essential' | 'lastSet'>): boolean => {
  // This is a bit of a trick to use the context outside of a React component.
  // It's generally better to use the useCookieConsentContext hook in components.
  // However, for non-component logic (like loading scripts), this can be an option,
  // BUT it relies on the provider being already initialized and the hook having run.
  // A more robust solution for non-component access might involve a singleton or event bus.
  // For now, let's assume this will be called after context is available or modify script loading to be component-aware.

  // THIS APPROACH IS NOT RECOMMENDED FOR DIRECT USE IN NON-REACT FILES.
  // It's better to pass the consent state from a component to the script loading logic.
  // I'll keep it here as a placeholder for discussion if needed, but the primary way
  // to check consent will be via the useCookieConsentContext hook in React components.
  
  // Instead of the above, components should use:
  // const { isCategoryEnabled } = useCookieConsentContext();
  // const analyticsAllowed = isCategoryEnabled('analytics');

  // For this step, we are just setting up the context provider and hook.
  // Script loading (step 5) will need to correctly use the context within React components
  // or have the consent status passed to it.
  console.warn("isCookieCategoryEnabled (direct context access) is not recommended for non-React files. Use useCookieConsentContext hook in components.");
  return false; // Placeholder, actual logic will be in hook via context.
};
